import { useEffect } from 'react'
import { Outlet, useLoaderData } from 'react-router'
import { Analytics } from './components/analytics'
import { CANONICAL_HOSTNAME, HEALTH_CHECK_PATH } from '~/config'
import type { LoaderFunctionArgs, MiddlewareFunction } from 'react-router'
import { data, redirect } from 'react-router'
import { isProduction } from './utils/is-production.server'
import { forwardedUrl } from './utils/forwarded-url'
import { rootLinks } from './components-v2/links'
import { SharedLayout } from './components/shared-layout'
import { SupportedLanguages } from './config/i18n'
import { findRedirect } from './config/redirects.server'
import {
  computeNextCookieData,
  cookieExpires,
  omniCookie,
  TrackingData,
} from './services/cookie.server'

export { ErrorBoundary } from '~/components'
export { meta } from '~/utils'

export const links = () => [
  ...rootLinks(),
  { rel: 'icon', href: '/favicon.png', type: 'image/png' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Cal+Sans&family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:ital,wght@0,100..700;1,100..700&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inconsolata:wght@400;500;600&display=swap',
  },
]

/**
 * Render web services have no edge redirect layer, so redirects run here.
 * Middleware runs ahead of every loader, so a redirected path never triggers
 * the catch-all route's Contentful lookup.
 */
export const middleware: MiddlewareFunction<Response>[] = [
  ({ request }) => {
    const url = new URL(request.url)

    // The platform probes this on an internal hostname, so the canonical-host
    // redirect below would answer it and the check would never reach a handler.
    if (url.pathname === HEALTH_CHECK_PATH) return

    if (isProduction(request) && url.hostname !== CANONICAL_HOSTNAME) {
      return redirect(
        `https://${CANONICAL_HOSTNAME}${url.pathname}${url.search}`,
        301,
      )
    }

    const appRedirect = findRedirect(url)
    if (appRedirect) return redirect(appRedirect.to, appRedirect.status)
  },
]

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = forwardedUrl(request)
  const { hasChanges, tracking } = await computeNextCookieData({ request })
  const expires = cookieExpires()
  return data(
    {
      /** @deprecated - remove soon */
      flash: undefined,
      isDevelopment: process.env.NODE_ENV === 'development',
      lang:
        url.pathname.split('/')[1] === 'fr'
          ? SupportedLanguages.French
          : SupportedLanguages.English,
      referrer: request.headers.get('Referer'),
      requestOrigin: url.origin,
      tracking,
    },
    {
      headers: hasChanges
        ? { 'Set-Cookie': await omniCookie.serialize(tracking, { expires }) }
        : undefined,
    },
  )
}

export type RootOutletContext = {
  tracking: TrackingData
}

export default function App() {
  const { lang, tracking } = useLoaderData<typeof loader>()

  /**
   * Effects only run once React has committed the hydrated tree, so this
   * attribute marks the moment event handlers are live. The e2e suite waits
   * for it before interacting; a click or hover that lands earlier is silently
   * dropped and can never be retried by an assertion.
   */
  useEffect(() => {
    document.documentElement.dataset.hydrated = 'true'
  }, [])

  return (
    <SharedLayout lang={lang}>
      <Analytics />
      <Outlet context={{ tracking }} />
    </SharedLayout>
  )
}
